import { Button, ContentLayout, Header, SpaceBetween, Tabs } from '@amzn/awsui-components-console';
import { pick } from 'lodash';
import moment from 'moment';
import { Helmet } from 'react-helmet';
import { useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';

import { CancelClass, ClassOverview, LabNavigation, LinkButton } from '@/components';
import { useGetLearningActivity } from '@/api/hooks';
import { messages } from '@/containers/classPages/ClassContainer/index.messages';
import {
    getPageId,
    getPageMetadata,
    type PageId,
} from '@/containers/classPages/ClassContainer/index.utils';
import ClassDetailPage from '@/containers/classPages/ClassDetailPage/ClassDetailPage';
import ClassNotesPage from '@/containers/classPages/ClassNotesPage';
import ClassPage from '@/containers/classPages/ClassPage/ClassPage';
import LabPage from '@/containers/labPage/Lab';
import { Layout } from '@/containers/Layout';
import { useProvider } from '@/data/ProviderContext';
import { isAttendanceListingEnabled } from '@/data/useAttendanceManagement';
import { useClassroomData } from '@/data/useClassroomData';
import { paths } from '@/routing/paths';
import { useFlags } from '@/utils';
import { getStudentURL } from '@/utils/helpers';
import { useDecodedParams } from '@/utils/paramHooks';

export const ClassContainer = () => {
    const { supportsActivities } = useFlags();
    const { formatMessage } = useIntl();
    const { pathname } = useLocation();
    const params = useDecodedParams();
    const classroomId = params.classroomId as string;
    const { labNumber } = params;
    const navigate = useNavigate();
    const provider = useProvider();
    const {
        hasNoLabs,
        classData: { classroom = {}, course = {}, content },
        isLoading: isClassroomLoading,
    } = useClassroomData(classroomId);
    const { data: learningActivityQuery, isLoading: isLearningActivityQueryLoading } =
        useGetLearningActivity();
    const learningActivityData = learningActivityQuery?.getLearningActivity;

    // Combine loading states - show spinner until both API calls complete
    const isLearningActivityLoading =
        isLearningActivityQueryLoading && !!classroom.learningActivityID;
    const isLoading = isClassroomLoading || isLearningActivityLoading;

    const courseTitle = course.title;
    const pageId = getPageId(pathname, { classroomId, labNumber });
    const { breadcrumbs, title } = getPageMetadata({
        pageId,
        classroomId,
        formatMessage,
        courseTitle,
    });

    const editIsDisabled = moment().isAfter(moment.unix(classroom.endsOn));
    const isAttendanceEnabled = isAttendanceListingEnabled({
        lms_type: learningActivityData?.lms_type,
    });

    const navigateToPage = (target: PageId) => {
        switch (target) {
            case 'dayOf':
                navigate(paths.classPage(classroomId));
                break;
            case 'lab':
                navigate(paths.labPage(classroomId, 1));
                break;
            case 'notes':
                navigate(paths.classNotesPage(classroomId));
                break;
            case 'detail':
            default:
                navigate(paths.classDetailPage(classroomId));
                break;
        }
    };

    return (
        <Layout
            breadcrumbItems={breadcrumbs}
            useMaxWidth
            navigation={
                pageId === 'lab' && !hasNoLabs ? (
                    <LabNavigation content={content} courseTitle={courseTitle} />
                ) : null
            }
        >
            <Helmet>{title}</Helmet>
            <ContentLayout
                header={
                    <Header
                        variant='h1'
                        actions={
                            <SpaceBetween direction='horizontal' size='s'>
                                <LinkButton
                                    disabled={editIsDisabled}
                                    ariaLabel={formatMessage(messages.editButtonLabel)}
                                    to={paths.classEditPage(classroomId)}
                                >
                                    {formatMessage(messages.editButtonText)}
                                </LinkButton>
                                {isAttendanceEnabled ? (
                                    <LinkButton
                                        ariaLabel={formatMessage(
                                            messages.classAttendanceButtonLabel,
                                        )}
                                        to={paths.classAttendance(classroomId)}
                                    >
                                        {formatMessage(messages.classAttendanceButtonText)}
                                    </LinkButton>
                                ) : null}
                                <CancelClass
                                    {...pick(classroom, [
                                        'classroomId',
                                        'isCancellable',
                                        'providerArn',
                                        'trainingProviderType',
                                    ])}
                                />
                                <Button
                                    ariaLabel={formatMessage(messages.externalLabel)}
                                    href={getStudentURL(classroomId)}
                                    iconAlign='right'
                                    iconName='external'
                                    target='_blank'
                                >
                                    {formatMessage(messages.studentView)}
                                </Button>
                            </SpaceBetween>
                        }
                    >
                        {courseTitle}
                    </Header>
                }
            >
                <SpaceBetween direction='vertical' size='l'>
                    <ClassOverview
                        isLoading={isLoading}
                        course={course}
                        classroom={classroom}
                        instructors={classroom.instructors}
                        provider={provider}
                        learningActivity={learningActivityData}
                    />
                    <Tabs
                        activeTabId={pageId}
                        onChange={({ detail }) => {
                            navigateToPage(detail.activeTabId as PageId);
                        }}
                        tabs={[
                            {
                                id: 'dayOf',
                                label: formatMessage(messages.dayOf),
                                content: <ClassPage />,
                            },
                            {
                                id: 'detail',
                                label: formatMessage(messages.classDetails),
                                content: <ClassDetailPage />,
                            },
                            {
                                id: 'lab',
                                disabled: !supportsActivities && hasNoLabs,
                                disabledReason: formatMessage(messages.classHasNoLabsReason),
                                label: formatMessage(messages.manageLabs),
                                content: <LabPage />,
                            },
                            ...(learningActivityData
                                ? [
                                      {
                                          id: 'notes',
                                          label: formatMessage(messages.classNotes),
                                          content: <ClassNotesPage />,
                                      },
                                  ]
                                : []),
                        ]}
                    />
                </SpaceBetween>
            </ContentLayout>
        </Layout>
    );
};
